import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";

import {
    ClockTime,
    DayOfWeek,
    FormDateTimeInput,
    ScheduleException,
    TimeOffering,
    TimeSlotSchedule,
} from "stentor-models";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/**
 * Minutes between generated starts when the schedule does not say.
 */
export const DEFAULT_SLOT_MINUTES = 30;

/**
 * How far ahead the calendar reaches when the schedule does not say.
 */
export const DEFAULT_HORIZON_DAYS = 60;

/**
 * Field name the chosen date is submitted under when the field does not say.
 *
 * Deliberately fixed rather than derived from the field's own name: every existing lead
 * consumer already reads `preferred_date`, and namespacing it per field would break all of
 * them to solve a problem only a two-DATETIME step has. A step carrying two of these must
 * set `dateFieldName` on at least one, or the second silently overwrites the first.
 */
export const DEFAULT_DATE_FIELD_NAME = "preferred_date";

/**
 * Field name the combined ISO timestamp is submitted under when the field does not say.
 *
 * Fixed for the same reason as {@link DEFAULT_DATE_FIELD_NAME}, and additionally because
 * `datetime` is the name the lead analyzer already recognises and ISO-validates. See
 * `dateTimeFieldName` to move it.
 */
export const DEFAULT_DATE_TIME_FIELD_NAME = "datetime";

/**
 * Field name the schedule's timezone is submitted under when the field does not say.
 *
 * Fixed for the same reason as {@link DEFAULT_DATE_FIELD_NAME}. It exists so a consumer
 * can tell a picker-produced `datetime` from a scraped one: an abandoned form contributes
 * fields literally named `date` or `appointment_time` straight into the lead with no
 * validation, and "carries an offset" cannot say who produced the value. This can.
 */
export const DEFAULT_TIME_ZONE_FIELD_NAME = "preferred_time_zone";

/**
 * `FormDateTimeInput` with the name its timezone field is submitted under.
 *
 * Declared here rather than read off the model because `timeZoneFieldName` is newer than
 * the `stentor-models` pinned by this package; it collapses back into `FormDateTimeInput`
 * once that version lands.
 */
export interface FormDateTimeInputWithTimeZone extends FormDateTimeInput {
    /**
     * Field name the schedule's IANA timezone is submitted under. Defaults to
     * "preferred_time_zone".
     */
    timeZoneFieldName?: string;
}

const MS_PER_DAY = 86400000;

/**
 * A fixed offset rather than a zone, e.g. "-04:00". Also catches a bare offset like "-4".
 */
const FIXED_OFFSET = /^[+-]?\d/;

/**
 * An arbitrary instant, only ever used to ask whether a zone identifier resolves at all.
 */
const ZONE_PROBE = "2026-01-01T00:00";

// Indexed to match Date#getUTCDay().
const WEEKDAYS: DayOfWeek[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

const CLOCK_TIME = /^(\d{1,2}):(\d{2})/;

/**
 * Minutes past midnight for a "HH:MM" clock time, or -1 for anything unparseable.
 *
 * A schedule is authored by hand in Studio, so a malformed time is a real possibility
 * and has to drop the span rather than generate slots at NaN.
 */
function clockToMinutes(clock: ClockTime): number {
    const match = CLOCK_TIME.exec(clock || "");

    if (!match) {
        return -1;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (hours > 23 || minutes > 59) {
        return -1;
    }

    return hours * 60 + minutes;
}

function pad(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
}

function minutesToClock(minutes: number): ClockTime {
    // Past midnight wraps, so a duration that runs over the end of the day still reads
    // as a time rather than as "24:30".
    const wrapped = ((minutes % 1440) + 1440) % 1440;

    return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

/**
 * The calendar date a `Date` represents, as "YYYY-MM-DD".
 *
 * Read off the local fields rather than through `toISOString`, which converts to UTC and
 * so reports the next day for an evening selection anywhere west of Greenwich.
 */
export function toCalendarDate(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The weekday a "YYYY-MM-DD" calendar date falls on.
 */
function weekdayOf(calendarDate: string): DayOfWeek | undefined {
    const parsed = new Date(`${calendarDate}T00:00:00Z`);

    return isNaN(parsed.getTime()) ? undefined : WEEKDAYS[parsed.getUTCDay()];
}

/**
 * Whole days from one calendar date to another, ignoring time of day entirely - so a
 * daylight-saving transition in between cannot make the answer come out fractional.
 */
function daysBetween(from: string, to: string): number {
    return Math.round(
        (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY
    );
}

/**
 * Whether a zone can actually be used to place a wall-clock time.
 *
 * A schedule's zone is typed in by hand in Studio, so "Eastern" or "America/New York"
 * reaches the picker as readily as a real IANA identifier does. Matching is case
 * insensitive, so "America/New_york" is fine.
 */
export function isUsableTimezone(timezone?: string): boolean {
    if (!timezone) {
        return false;
    }

    try {
        new Intl.DateTimeFormat("en-US", { timeZone: timezone });

        return true;
    } catch (err) {
        return false;
    }
}

/**
 * The zone the visitor's own browser is in, or "" where that cannot be determined.
 */
export function hostTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (err) {
        return "";
    }
}

// Generic first, so a zone reads as "Eastern Time" rather than as whichever side of a
// daylight-saving transition today happens to fall on - the times below it span both.
// "long" is the fallback for a platform too old to know the generic form.
const TIMEZONE_NAME_STYLES = ["longGeneric", "long"];

/**
 * A zone named the way a visitor reads it, e.g. "America/New_York" to "Eastern Time".
 *
 * Falls back to the identifier itself rather than to nothing, because an unnamed zone
 * still tells a visitor more than silence does.
 */
export function formatTimezoneLabel(timezone?: string): string {
    if (!timezone) {
        return "";
    }

    for (const timeZoneName of TIMEZONE_NAME_STYLES) {
        try {
            const parts = new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                timeZoneName,
            } as Intl.DateTimeFormatOptions).formatToParts(new Date());
            const named = parts.find((part) => part.type === "timeZoneName");

            if (named && named.value) {
                return named.value;
            }
        } catch (err) {
            // Either the zone or the naming style is unsupported here. Try the next
            // style, then give up on naming it at all.
        }
    }

    return timezone;
}

/**
 * The instant a wall-clock time on a calendar date happens, in the schedule's zone.
 *
 * An unknown zone falls back to the host's, because a picker that renders slightly wrong
 * times is better than one that throws while the visitor is looking at it.
 */
function zonedInstant(calendarDate: string, clock: ClockTime, timezone: string): number {
    const local = `${calendarDate}T${clock}`;

    try {
        return dayjs.tz(local, timezone).valueOf();
    } catch (err) {
        return dayjs(local).valueOf();
    }
}

/**
 * An offset-bearing ISO 8601 timestamp for a wall-clock time in the given zone, e.g.
 * "2026-08-24T09:30:00-04:00". The offset is the one in force on that date, so the value
 * survives a daylight-saving transition.
 */
export function toOffsetIso(calendarDate: string, clock: ClockTime, timezone: string): string {
    const minutes = clockToMinutes(clock);
    const normalized = minutes < 0 ? clock : minutesToClock(minutes);
    const local = `${calendarDate}T${normalized}`;

    try {
        return dayjs.tz(local, timezone).format();
    } catch (err) {
        return dayjs(local).format();
    }
}

/**
 * The zone the picker really built its times in, as the IANA identifier the schedule
 * authored - or nothing at all when it did not.
 *
 * {@link zonedInstant} and {@link toOffsetIso} both fall back to the host's zone rather
 * than throwing at a visitor mid-form, so an absent or unrecognised `timezone` still
 * produces a full column of times: they are simply the wrong ones. Naming the business's
 * zone beside those would hand a consumer a marker it cannot trust, and a wrong marker is
 * worse than none - the entire point of the field is that it can be trusted - so nothing
 * is reported instead.
 *
 * A fixed offset is refused for the same reason. `Intl` accepts "-04:00", so the times
 * generated from it are self-consistent, but an offset does not survive a daylight-saving
 * transition and so cannot stand in for the zone.
 */
export function timeZoneOf(schedule: TimeSlotSchedule): string | undefined {
    const timezone = schedule?.timezone;

    if (!timezone || typeof timezone !== "string" || FIXED_OFFSET.test(timezone)) {
        return undefined;
    }

    try {
        // The same call zonedInstant makes, so the two cannot disagree about which zones
        // are usable and the marker cannot outlive the fallback it is meant to detect.
        if (!dayjs.tz(ZONE_PROBE, timezone).isValid()) {
            return undefined;
        }
    } catch (err) {
        return undefined;
    }

    return timezone;
}

/**
 * A 12-hour rendering of a clock time, e.g. "09:30" to "9:30 AM".
 */
function formatClock(clock: ClockTime): string {
    const minutes = clockToMinutes(clock);

    if (minutes < 0) {
        return clock;
    }

    const hours24 = Math.floor(minutes / 60);
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

    return `${hours12}:${pad(minutes % 60)} ${hours24 < 12 ? "AM" : "PM"}`;
}

/**
 * What an offering reads as in the time column - and, because the label is what the field
 * submits, what a CRM or lead consumer receives for it.
 */
export function formatOfferingLabel(offering: TimeOffering): string {
    if (!offering) {
        return "";
    }

    if (offering.label) {
        return offering.label;
    }

    const start = formatClock(offering.start);

    return offering.end ? `${start} - ${formatClock(offering.end)}` : start;
}

/**
 * How far ahead the calendar reaches, so the picker's own maxDate and the selectability
 * rules cannot drift apart.
 */
export function horizonDaysOf(schedule: TimeSlotSchedule): number {
    const horizon = schedule?.horizonDays;

    // 0 is a real answer here - "today only" - unlike slotMinutes or durationMinutes where
    // it is meaningless. Testing truthiness handed a same-day-only business the 60 day
    // default instead, which is the opposite of what they asked for.
    return typeof horizon === "number" && horizon >= 0 ? horizon : DEFAULT_HORIZON_DAYS;
}

function findException(
    schedule: TimeSlotSchedule,
    calendarDate: string
): ScheduleException | undefined {
    return (schedule.exceptions || []).find((exception) => exception.date === calendarDate);
}

/**
 * Drop the offerings that have already passed, measured in the schedule's zone rather than
 * the visitor's.
 *
 * A time earlier today is never bookable, whether or not the schedule sets a lead time —
 * `leadTimeMinutes` pushes the cutoff further out, it does not create it. Treating lead time
 * as the only mechanism let a schedule that omitted it offer 9:00 AM at 3 in the afternoon.
 */
function filterElapsed(
    offerings: TimeOffering[],
    schedule: TimeSlotSchedule,
    calendarDate: string,
    now: Date
): TimeOffering[] {
    const lead = schedule.leadTimeMinutes;
    const buffer = lead && lead > 0 ? lead : 0;
    const cutoff = now.getTime() + buffer * 60000;

    return offerings.filter(
        (offering) => zonedInstant(calendarDate, offering.start, schedule.timezone) >= cutoff
    );
}

/**
 * The times a schedule offers on one calendar date.
 *
 * Slots come from the schedule alone - there is no availability lookup behind this, so a
 * date change repaints the column without a round trip. The rules, in order:
 *
 * 1. `offerings` present replaces generation. `days`, `slotMinutes` and `durationMinutes`
 *    are not consulted. A closing exception still closes the date, because that is the
 *    only way a fixed offering list can express a holiday.
 * 2. Otherwise the weekday's spans, or the spans of an `exceptions` entry for this exact
 *    date, which *replace* the weekday's rather than adding to them. No spans is closed.
 * 3. Each span is walked in `slotMinutes` steps, up to but not including its end.
 * 4. `durationMinutes` gives every offering an `end`; without it they are discrete starts.
 *    A start whose appointment would run past the span's end is not offered at all.
 * 5. Whatever survives is filtered against now, plus `leadTimeMinutes` if the schedule
 *    sets one. A time earlier today is never offered.
 *
 * @param schedule The schedule to read.
 * @param date     The calendar day being offered, read off its local fields.
 * @param now      The current moment. Injectable so the lead-time filter is testable.
 */
export function generateOfferings(
    schedule: TimeSlotSchedule,
    date: Date,
    now: Date = new Date()
): TimeOffering[] {
    if (!schedule || !date || isNaN(date.getTime())) {
        return [];
    }

    const calendarDate = toCalendarDate(date);
    const exception = findException(schedule, calendarDate);

    if (Array.isArray(schedule.offerings)) {
        // No spans reads as closed here exactly as it does on the generated path below.
        // `spans` is required by the type, but a schedule is authored in Studio and arrives
        // as config, so `{ date: "2026-12-25" }` used as a holiday marker has to close the
        // date rather than quietly fall through to the normal offerings.
        if (exception && (!exception.spans || exception.spans.length === 0)) {
            return [];
        }

        return filterElapsed(schedule.offerings, schedule, calendarDate, now);
    }

    const weekday = weekdayOf(calendarDate);
    const spans = exception
        ? exception.spans || []
        : (weekday && (schedule.days || {})[weekday]) || [];

    const step =
        schedule.slotMinutes && schedule.slotMinutes > 0
            ? schedule.slotMinutes
            : DEFAULT_SLOT_MINUTES;
    const duration =
        schedule.durationMinutes && schedule.durationMinutes > 0
            ? schedule.durationMinutes
            : undefined;

    const offerings: TimeOffering[] = [];

    for (const span of spans) {
        const start = clockToMinutes(span?.start);
        const end = clockToMinutes(span?.end);

        if (start < 0 || end < 0) {
            continue;
        }

        for (let minutes = start; minutes < end; minutes += step) {
            // The whole appointment has to fit inside the span, which is what Calendly does:
            // 9:00-12:00 at 60 minutes offers 11:00 last, never 11:30. Clipping the end
            // instead would keep the slot but advertise a length the business is not open
            // for, and a span exists precisely to protect what follows it - a lunch break,
            // or closing time.
            if (duration && minutes + duration > end) {
                break;
            }

            const offering: TimeOffering = { start: minutesToClock(minutes) };

            if (duration) {
                offering.end = minutesToClock(minutes + duration);
            }

            offerings.push(offering);
        }
    }

    return filterElapsed(offerings, schedule, calendarDate, now);
}

/**
 * Whether the calendar should let a date be picked.
 *
 * False for anything outside the horizon - in the past, or further ahead than
 * `horizonDays` - and for any date the schedule generates no offerings for, so a closed
 * day greys out rather than opening onto an empty column.
 *
 * @param schedule The schedule to read.
 * @param date     The calendar day being tested, read off its local fields.
 * @param now      The current moment. Injectable so the horizon is testable.
 */
export function isDateSelectable(
    schedule: TimeSlotSchedule,
    date: Date,
    now: Date = new Date()
): boolean {
    if (!schedule || !date || isNaN(date.getTime())) {
        return false;
    }

    const calendarDate = toCalendarDate(date);
    const horizon = horizonDaysOf(schedule);

    // Today is the business's today, not the visitor's - they are frequently different days.
    const today = todayInZone(schedule.timezone, now);
    const offset = daysBetween(today, calendarDate);

    if (offset < 0 || offset > horizon) {
        return false;
    }

    return generateOfferings(schedule, date, now).length > 0;
}

/**
 * The calendar date it currently is in the given zone.
 */
function todayInZone(timezone: string, now: Date): string {
    try {
        return dayjs(now).tz(timezone).format("YYYY-MM-DD");
    } catch (err) {
        return toCalendarDate(now);
    }
}
